// Atlas Dashboard — Contacts Page
// Lists all contacts with engagement trends and search.

import { useState } from "react";
import { Users, Search, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { useContacts } from "../hooks/useApi";
import { formatIstDate, formatPercent, truncate } from "../lib/format";
import { ENGAGEMENT_TREND_CONFIG } from "@atlas/shared";
import type { Contact, EngagementTrend } from "@atlas/shared";

export function ContactsPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const { data, loading, error } = useContacts(page, search || undefined);

  if (loading) return <div style={{ padding: "48px", textAlign: "center", color: "var(--color-text-tertiary)" }}>Loading contacts...</div>;
  if (error) return <div style={{ padding: "48px", textAlign: "center", color: "#DC2626" }}>{error}</div>;
  if (!data || data.items.length === 0) return <EmptyState search={search} />;

  return (
    <div>
      {/* Search bar */}
      <div style={{ marginBottom: "24px" }}>
        <div style={{ position: "relative", maxWidth: "400px" }}>
          <Search size={16} style={{ position: "absolute", left: "12px", top: "10px", color: "var(--color-text-tertiary)" }} />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{
              width: "100%",
              padding: "10px 12px 10px 36px",
              border: "1px solid var(--color-border)",
              borderRadius: "8px",
              fontSize: "14px",
              backgroundColor: "var(--color-card)",
              color: "var(--color-text-primary)",
              outline: "none",
              boxSizing: "border-box" as const,
            }}
          />
        </div>
      </div>

      {/* Contact table */}
      <div style={{ backgroundColor: "var(--color-card)", borderRadius: "8px", border: "1px solid var(--color-border)", overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
          <thead>
            <tr style={{ backgroundColor: "#F9FAFB", borderBottom: "1px solid var(--color-border)" }}>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>Engagement</th>
              <th style={thStyle}>Last Contact</th>
              <th style={thStyle}>Reply Rate</th>
              <th style={thStyle}>Open Rate</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((contact: Contact) => (
              <ContactRow key={contact.contactId} contact={contact} />
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: "16px", fontSize: "13px", color: "var(--color-text-tertiary)" }}>
        {data.total} contacts • Page {data.page}
      </div>
    </div>
  );
}

function ContactRow({ contact }: { contact: Contact }) {
  const trendConfig = ENGAGEMENT_TREND_CONFIG[contact.engagementTrend as EngagementTrend];
  const TrendIcon = contact.engagementTrend === "warming" ? TrendingUp : contact.engagementTrend === "cooling" ? TrendingDown : Minus;

  return (
    <tr
      style={{ borderBottom: "1px solid var(--color-border)", cursor: "pointer", transition: "background 0.15s" }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#F9FAFB")}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
    >
      <td style={tdStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {contact.goingQuiet === true && <span style={{ color: "#D97706" }} title="Going quiet">●</span>}
          {contact.name || contact.email.split("@")[0]}
        </div>
      </td>
      <td style={{ ...tdStyle, color: "var(--color-text-secondary)", fontSize: "13px" }}>{truncate(contact.email, 35)}</td>
      <td style={tdStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <TrendIcon size={14} color={trendConfig?.color === "green" ? "#16A34A" : trendConfig?.color === "amber" ? "#D97706" : "#6B7280"} />
          <span style={{ fontSize: "13px" }}>{trendConfig?.label}</span>
        </div>
      </td>
      <td style={{ ...tdStyle, color: "var(--color-text-tertiary)", fontSize: "13px" }}>{formatIstDate(contact.lastContactAt)}</td>
      <td style={tdStyle}>{formatPercent(contact.replyRate)}</td>
      <td style={tdStyle}>{formatPercent(contact.openRate)}</td>
    </tr>
  );
}

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "12px 16px",
  fontSize: "12px",
  fontWeight: 600,
  color: "var(--color-text-tertiary)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const tdStyle: React.CSSProperties = {
  padding: "12px 16px",
  color: "var(--color-text-primary)",
};

function EmptyState({ search }: { search: string }) {
  return (
    <div style={{ padding: "48px", textAlign: "center" }}>
      <Users size={48} style={{ color: "var(--color-text-tertiary)", margin: "0 auto 16px" }} />
      <h3 style={{ fontSize: "16px", fontWeight: 600, marginBottom: "8px" }}>
        {search ? `No contacts found for "${search}"` : "No contacts yet"}
      </h3>
      <p style={{ color: "var(--color-text-secondary)", fontSize: "14px" }}>
        {search ? "Try a different search term." : "Once you send tracked emails, Atlas will automatically build profiles for the people you email."}
      </p>
    </div>
  );
}
